import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

const BODY_CLASS = 'fy-wizard-coach-active';

/**
 * Tracks when a smart wizard coach overlay is mounted (highlight + coach mark).
 * Drives ng-select `dropdownPosition`: open away from the coach card
 * (coach above field → panel down; coach below field → panel up).
 */
@Injectable({ providedIn: 'root' })
export class WizardCoachOverlayUiService {
  private readonly platformId = inject(PLATFORM_ID);
  private depth = 0;
  /** True while at least one wizard highlight overlay instance is active. */
  readonly active = signal(false);
  /**
   * While overlay is shown: 'top' = open panel above input, 'bottom' = below.
   * Set by wizard hosts from `isPlacedAbove` (coach above target → 'bottom', else 'top').
   */
  readonly ngSelectDropdownPosition = signal<'auto' | 'top' | 'bottom'>('auto');

  beginWizardCoachOverlay(): void {
    const wasInactive = this.depth === 0;
    this.depth += 1;
    if (wasInactive) {
      this.active.set(true);
      this.ngSelectDropdownPosition.set('auto');
      this.setBodyClass(true);
    }
  }

  endWizardCoachOverlay(): void {
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth === 0) {
      this.active.set(false);
      this.ngSelectDropdownPosition.set('auto');
      this.setBodyClass(false);
    }
  }

  /**
   * @param coachMarkIsAboveTarget true when the coach card sits above the highlighted control
   */
  syncNgSelectPanelAwayFromCoach(coachMarkIsAboveTarget: boolean): void {
    if (this.depth === 0) {
      return;
    }
    this.ngSelectDropdownPosition.set(coachMarkIsAboveTarget ? 'bottom' : 'top');
  }

  /** For templates: honor explicit top/bottom; else use wizard hint when overlay is active. */
  resolveNgSelectDropdownPosition(explicit: 'auto' | 'top' | 'bottom'): 'auto' | 'top' | 'bottom' {
    if (explicit === 'top' || explicit === 'bottom') {
      return explicit;
    }
    if (this.depth === 0) {
      return 'auto';
    }
    const p = this.ngSelectDropdownPosition();
    // Overlay sets 'auto' until wizard host effect runs; ng-select 'auto' often opens down and covers the coach.
    if (p === 'auto') {
      return 'top';
    }
    return p;
  }

  /**
   * ngx-daterangepicker `drops`: default open **up** (like header fiscal-year ng-select).
   * When wizard coach is active, mirror {@link syncNgSelectPanelAwayFromCoach}:
   * coach above target → open **down**; coach below → open **up**.
   */
  resolveDatepickerDrops(explicit: 'auto' | 'up' | 'down'): 'up' | 'down' {
    if (explicit === 'up' || explicit === 'down') {
      return explicit;
    }
    if (this.depth === 0) {
      return 'up';
    }
    const p = this.ngSelectDropdownPosition();
    if (p === 'auto') {
      return 'up';
    }
    return p === 'bottom' ? 'down' : 'up';
  }

  private setBodyClass(on: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.body.classList.toggle(BODY_CLASS, on);
  }
}
